import { sharePage } from '@repo/common-utils';
import { useWorkingPlatform } from '@repo/platform';
import { useUserEventLogger } from '@repo/tracking-service';
import { useToast, Button, Description } from '@zenterprise-inc/ui';
import { useRouter } from 'next/router';

import { ApplyCompleteBanner } from '@/components/common/ApplyCompleteBanner';
import { CompleteCta } from '@/components/tax-refund/apply/complete/CompleteCta';
import { getCorporationRefundData } from '@/components/tax-refund/common/data/corporation-refund-data';
import { getIndisSearchRefundData } from '@/components/tax-refund/common/data/indis-search-refund-data';
import { isInviteEventProceeding } from '@/lib/constants/event';
import { INVITE_PROMOTION_PATH, 비즈넵환급서비스공유주소 } from '@/lib/constants/link';
import { COMMON_PATHS } from '@/lib/constants/paths';
import { useMarketingLogSend } from '@/lib/hooks/use-marketing-log-send';
import { BusinessType } from '@/lib/types/request';

export const ApplyCompleteContent = ({ businessType }: { businessType: BusinessType }) => {
  const router = useRouter();
  const toast = useToast();
  const { workingPlatform, workingPlatformType } = useWorkingPlatform();
  const { sendClickEvent } = useUserEventLogger();

  // 개인 환급 완료 관련
  const indisRefundAmount = getIndisSearchRefundData()?.refundAmount;
  // 법인 환급 완료 관련
  const corpRefundAmount = getCorporationRefundData()?.refundAmount;

  useMarketingLogSend('신청완료', businessType === 'indis' ? indisRefundAmount : corpRefundAmount);

  // 법인: 확인 버튼 클릭
  const handleClick = () => {
    sendClickEvent('corps-request_apply-complete_finish');
    router.push({ pathname: COMMON_PATHS.환급조회목록, query: router.query });
  };

  // 법인: 공유 버튼 클릭
  const handleClickShare = () => {
    sendClickEvent('corps-request_apply-complete_share');
    if (isInviteEventProceeding) {
      router.push({ pathname: INVITE_PROMOTION_PATH, query: router.query });
      return;
    }
    sharePage(
      {
        title: '개인/법인사업자 환급금 무료 조회 - 비즈넵 세금 환급',
        url: 비즈넵환급서비스공유주소
      },
      () => {
        toast({ content: '링크를 복사했습니다.', icon: 'check-mark', iconColor: 'green.600' });
      }
    );
  };

  return (
    <div className="flex h-[calc(100vh-100px)] flex-col items-center justify-center pb-[108px]">
      <Description.Guide
        imgChild={<img src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}/common/iconography/icg_check_sm.svg`} className={'w-20'} alt={''} />}
        subTitle="환급 신청을 완료했어요!"
        description={'진행 과정은 카카오톡 알림톡으로\n안내해드릴게요.'}
      />
      {businessType === 'corps' && workingPlatform !== 'kbank' && <ApplyCompleteBanner />}
      <Button.StickyBottom.Wrapper className="page-padding">
        {businessType === 'indis' ? (
          <CompleteCta businessType={businessType} />
        ) : (
          <>
            <Button.StickyBottom.Button state={workingPlatformType === 'web' ? 'basic' : 'primary'} onClick={handleClick}>
              확인
            </Button.StickyBottom.Button>
            {workingPlatformType === 'web' && (
              <Button.StickyBottom.Button state="primary" onClick={handleClickShare}>
                {isInviteEventProceeding ? '친구 초대하기' : '공유하기'}
              </Button.StickyBottom.Button>
            )}
          </>
        )}
      </Button.StickyBottom.Wrapper>
    </div>
  );
};
